import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Steps, Button, Space, Form, InputNumber, Spin, Empty, Descriptions, Alert, Result } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { showSuccessMessage, showErrorMessage } from '../../services/messages';
import DrumQuantities from '../../components/drum/DrumQuantities';
import { WizardHeader, QuantityStep, useIsOperator, FLOW_COLORS, primaryButtonStyle, BackToHome } from '../../components/drum/WizardParts';
import MachineSelector from '../../components/machine/MachineSelector';
import { warnIfMachineNearCapacity } from '../../components/machine/machineOccupancy';
import EDrumStatus from '../../enums/EDrumStatus';

/**
 * Wizard didático de processamento do tambor.
 *
 * Layout adaptado por papel:
 *  - Operador (mobile/PWA): cabeçalho compacto (código + "Etapa X de N" + barra
 *    de progresso) e UM campo por passo (Máquina, Solvente, Borra, Água,
 *    Faltando, Temperatura) — facilita a digitação em tablet/celular.
 *  - Admin (desktop): mantém o <Steps> e o <DrumQuantities> agrupado.
 *
 * A máquina onde o tambor é processado é obrigatória. A escolha entre
 * "Recarga" e "Processamento" acontece no início do fluxo (antes de ler o
 * tambor), na Home do operador — quem escolhe "Recarga" vai ao fluxo dedicado
 * (RcConsumptionPanel); quem escolhe "Processamento" chega a este wizard.
 *
 * Todos os campos numéricos usam inputMode="decimal" para abrir o teclado
 * numérico no mobile. Salva via api.drum.process (registra usuário, data/hora,
 * máquina e AuditLog no back-end).
 */
const ProcessDrum = () => {
  const { idDrum } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isOperator = useIsOperator();
  const flowColor = location.state?.color || FLOW_COLORS.process;

  const [form] = Form.useForm();
  const [drum, setDrum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(0);
  const [done, setDone] = useState(false);
  // Máquina da cadeia atual: vem via navigate state (repassada pelo DrumEntry
  // ao longo da cadeia "processar outro"). Null ao iniciar o fluxo da Home —
  // o operador escolhe a máquina de novo a cada nova entrada no fluxo.
  const [machine, setMachine] = useState(() => location.state?.machine || null);

  useEffect(() => { loadDrum(); }, [idDrum]);

  // Avisa se a máquina escolhida está com ocupação elevada (>= 90% da capacidade)
  // ao iniciar o processamento do tambor (após ler o QR) ou ao trocar de máquina.
  // Não bloqueia — o operador decide se continua ou descarrega antes.
  useEffect(() => {
    if (machine?.id) warnIfMachineNearCapacity(machine.id);
  }, [machine?.id]);

  const loadDrum = async () => {
    setLoading(true);
    try {
      const response = await api.drum.get(idDrum);
      const d = response.data;
      setDrum(d);
      // Defesa em profundidade: tambor já processado → view só-leitura.
      if (d?.status === EDrumStatus.PROCESSED.code) {
        navigate(`/drums/${idDrum}/view`, { replace: true });
        return;
      }
    } catch (error) {
      showErrorMessage(error?.response?.data?.error || 'Erro ao carregar tambor.');
    } finally {
      setLoading(false);
    }
  };

  const values = Form.useWatch([], form);
  const total = (Number(values?.processing_solvent) + Number(values?.processing_borra) + Number(values?.processing_water) + Number(values?.processing_missing)) || 0;
  const hasTemp = values?.oil_temperature != null && Number(values.oil_temperature) > 0;

  const qtyFilled = () => [values?.processing_solvent, values?.processing_borra, values?.processing_water, values?.processing_missing]
    .every(v => v != null && v !== '' && !Number.isNaN(Number(v)));

  const handleConfirm = async () => {
    if (!machine?.id) {
      showErrorMessage('Selecione a máquina onde o tambor será processado.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        machine_id: machine.id,
        processing_solvent: values.processing_solvent,
        processing_borra: values.processing_borra,
        processing_water: values.processing_water,
        processing_missing: values.processing_missing,
        oil_temperature: values.oil_temperature,
      };
      await api.drum.process(idDrum, body);

      setDone(true);
      showSuccessMessage('Processamento salvo com sucesso!');
    } catch (error) {
      showErrorMessage(error?.response?.data?.error || 'Erro ao salvar processamento.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spin />;
  if (!drum) return <Empty description="Tambor não encontrado." />;
  if (done) {
    return (
      <Card>
        <Result
          status="success"
          title="Processamento concluído!"
          subTitle={`Tambor ${drum.code} processado na máquina ${machine?.code || '—'} e registrado no histórico.`}
          extra={[
            <Button
              type="primary"
              key="entry"
              size={isOperator ? 'large' : 'middle'}
              onClick={() => navigate('/entry', { state: { flow: 'process', color: flowColor, machine } })}
              style={isOperator ? primaryButtonStyle(flowColor) : undefined}
            >
              Processar outro tambor
            </Button>,
            isOperator ? (
              <Button key="home" size="large" onClick={() => navigate('/home')}>Voltar para a Home</Button>
            ) : (
              <Button key="list" onClick={() => navigate('/drums')}>Ver tambores</Button>
            )
          ]}
        />
      </Card>
    );
  }

  // Etapas de quantidade: um campo por passo no mobile (operador); agrupado no admin.
  const qtySteps = isOperator
    ? [
        {
          title: 'Solvente sujo',
          content: <QuantityStep form={form} name="processing_solvent" label="Solvente sujo" hint="Quantidade em litros." />,
          validate: ['processing_solvent'],
          canNext: () => values?.processing_solvent != null && values.processing_solvent !== '',
        },
        {
          title: 'Borra',
          content: <QuantityStep form={form} name="processing_borra" label="Borra" hint="Quantidade em litros." />,
          validate: ['processing_borra'],
          canNext: () => values?.processing_borra != null && values.processing_borra !== '',
        },
        {
          title: 'Água',
          content: <QuantityStep form={form} name="processing_water" label="Água" hint="Quantidade em litros." />,
          validate: ['processing_water'],
          canNext: () => values?.processing_water != null && values.processing_water !== '',
        },
        {
          title: 'Faltando',
          content: <QuantityStep form={form} name="processing_missing" label="Faltando" hint="Quantidade em litros." />,
          validate: ['processing_missing'],
          canNext: () => values?.processing_missing != null && values.processing_missing !== '',
        },
      ]
    : [
        {
          title: 'Quantidades',
          content: <DrumQuantities form={form} prefix="processing_" strict={false} />,
          validate: ['processing_solvent', 'processing_borra', 'processing_water', 'processing_missing'],
          canNext: qtyFilled,
        },
      ];

  const steps = [
    {
      title: 'Máquina',
      content: (
        <div>
          <div style={{ fontSize: 15, marginBottom: 8, color: '#374151' }}>
            Máquina onde o tambor será processado
          </div>
          <MachineSelector value={machine} onChange={setMachine} required color={flowColor} />
        </div>
      ),
      canNext: () => !!machine?.id,
    },
    {
      title: 'Tambor',
      content: (
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="Código"><strong>{drum.code}</strong></Descriptions.Item>
          <Descriptions.Item label="Cliente">{drum.client?.company_name || drum.client?.nickname || '—'}</Descriptions.Item>
          <Descriptions.Item label="Lote">#{drum.batch_id}</Descriptions.Item>
          <Descriptions.Item label="Status">{EDrumStatus.getByCode(drum.status)?.description}</Descriptions.Item>
        </Descriptions>
      ),
    },
    ...qtySteps,
    {
      title: 'Temperatura',
      content: isOperator ? (
        <QuantityStep
          form={form}
          name="oil_temperature"
          label="Temperatura do óleo"
          unit="°C"
          max={500}
          hint="Temperatura em graus Celsius."
        />
      ) : (
        <Form.Item
          label="Temperatura (°C)"
          name="oil_temperature"
          rules={[{ required: true, message: 'Informe a temperatura do óleo.' }]}
        >
          <InputNumber min={0} max={500} step={0.1} style={{ width: '100%' }} aria-label="Temperatura do óleo em graus Celsius" decimalSeparator="," inputMode="decimal" />
        </Form.Item>
      ),
      validate: ['oil_temperature'],
      canNext: () => hasTemp,
    },
    {
      title: 'Confirmar',
      content: (
        <div>
          {total !== 200 && (
            <Alert
              style={{ marginBottom: 16 }}
              type="warning"
              showIcon
              message={total > 200
                ? `Atenção: o total informado (${total.toFixed(2).replace('.', ',')} L) passou ${(total - 200).toFixed(2).replace('.', ',')} L dos 200 L. Confirme se os valores estão corretos.`
                : `Atenção: o total informado (${total.toFixed(2).replace('.', ',')} L) ficou ${(200 - total).toFixed(2).replace('.', ',')} L abaixo dos 200 L. Confirme se os valores estão corretos.`}
            />
          )}
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Máquina">{machine ? `${machine.code} — ${machine.name}` : '—'}</Descriptions.Item>
            <Descriptions.Item label="Solvente sujo">{values?.processing_solvent} L</Descriptions.Item>
            <Descriptions.Item label="Borra">{values?.processing_borra} L</Descriptions.Item>
            <Descriptions.Item label="Água">{values?.processing_water} L</Descriptions.Item>
            <Descriptions.Item label="Faltando">{values?.processing_missing} L</Descriptions.Item>
            <Descriptions.Item label="Temp. óleo">{values?.oil_temperature} °C</Descriptions.Item>
            <Descriptions.Item label="Total">{total.toFixed(2).replace('.', ',')} L</Descriptions.Item>
          </Descriptions>
          <Alert style={{ marginTop: 16 }} type="info" showIcon message="Confira os dados acima. Ao confirmar, o processamento será salvo com seu usuário, a máquina e a data/hora atuais." />
        </div>
      ),
    },
  ];

  const step = steps[current];
  const canNext = () => (typeof step.canNext === 'function' ? step.canNext() : true);

  const next = async () => {
    if (step.validate) {
      try { await form.validateFields(step.validate); }
      catch { return; }
    }
    setCurrent(c => Math.min(c + 1, steps.length - 1));
  };

  const prev = () => setCurrent(c => Math.max(c - 1, 0));

  return (
    <div style={{ maxWidth: isOperator ? 720 : 760, margin: '0 auto' }}>
      {isOperator && <BackToHome color={flowColor} />}
      <Card>
        {isOperator ? (
          <WizardHeader drumCode={drum.code} current={current} total={steps.length} title={step.title} color={flowColor} />
        ) : (
          <>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Processamento do tambor {drum.code}</div>
            <Steps
              current={current}
              size="small"
              items={steps.map(s => ({ title: s.title }))}
              style={{ marginBottom: 24 }}
            />
          </>
        )}
        <Form form={form} layout="vertical" preserve>
          {/* Mantém todas as etapas montadas (apenas escondidas) para que os
              valores dos Form.Item persistam e o Form.useWatch([]) enxergue os
              campos na etapa de confirmação. */}
          {steps.map((s, idx) => (
            <div key={idx} style={{ display: idx === current ? 'block' : 'none' }}>
              {s.content}
            </div>
          ))}
        </Form>
        <Space style={{ marginTop: 24, width: '100%', justifyContent: 'space-between' }} wrap>
          {current > 0 ? (
            <Button size={isOperator ? 'large' : 'middle'} onClick={prev}>Voltar</Button>
          ) : <span />}
          {current < steps.length - 1 && (
            <Button
              type="primary"
              size={isOperator ? 'large' : 'middle'}
              disabled={!canNext()}
              onClick={next}
              style={isOperator ? { minWidth: 160, ...primaryButtonStyle(flowColor) } : undefined}
            >
              Avançar
            </Button>
          )}
          {current === steps.length - 1 && (
            <Button
              type="primary"
              size={isOperator ? 'large' : 'middle'}
              icon={<CheckCircleOutlined />}
              loading={saving}
              onClick={handleConfirm}
              style={isOperator ? { minWidth: 200, ...primaryButtonStyle(flowColor) } : undefined}
            >
              Confirmar e salvar
            </Button>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default ProcessDrum;
